import React, { useRef, useState, useCallback, useEffect } from "react";
import styles from "../../styles/components/MailFlyer.module.css";
import { tick, cross } from "../../assets/icons";
import { mailsService } from "../../services/mails.service";
import Avatar from "../../ui/avatar/Avatar.jsx";
import LoadingScreen from "../../ui/loadingScreen/LoadingScreen";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import { extractApiErrorMessage } from "../../utils/apiError";

const THRESHOLD = 140;
const BUFFER_SIZE = 3;
const MAX_APPROVAL_MESSAGES = 5;

const RECIPIENT_STATE_LABELS = {
  valid: { to: "validado", cc: "validado" },
  validating: { to: "validando", cc: "validando" },
  unverified: { to: "se valida al aprobar", cc: "se valida al aprobar" },
  invalid: { to: "inválido: no se puede aprobar", cc: "inválido: no se copia" },
};

function recipientStateLabel(recipient) {
  const labelsByRole = RECIPIENT_STATE_LABELS[recipient.state];
  return labelsByRole ? labelsByRole[recipient.role] || labelsByRole.to : "";
}

function serverAnsweredWithClientError(error) {
  const responseStatus = error?.response?.status;
  return typeof responseStatus === "number" && responseStatus >= 400 && responseStatus < 500;
}

function mapCardItem(item) {
  return {
    id: item.id,
    subject: item.subject || "(Sin asunto)",
    body: item.body || "",
    raw: item,
  };
}

function formatDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const day = date.getDate();
  const ordinalSuffix =
    day === 1 || day === 21 || day === 31 ? "st"
    : day === 2 || day === 22 ? "nd"
    : day === 3 || day === 23 ? "rd"
    : "th";
  const month = date.toLocaleDateString("en-US", { month: "long" });
  return `${month} ${day}${ordinalSuffix}, ${date.getFullYear()}`;
}

const MailFlyer = ({ responsibleFilterId = null }) => {
  const [cards, setCards] = useState([]);
  const [errorMails, setErrorMails] = useState([]);
  const [approvalMessages, setApprovalMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refilling, setRefilling] = useState(false);

  const [tx, setTx] = useState(0);
  const [rot, setRot] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [fly, setFly] = useState(null);
  const startRef = useRef({ x: 0 });
  const mountedRef = useRef(true);
  const knownCardIdsRef = useRef(new Set());
  const knownErrorIdsRef = useRef(new Set());
  const knownPendingCountRef = useRef(0);
  const refillPromiseRef = useRef(null);
  const refillGenerationRef = useRef(0);

  const visible = cards.slice(0, 3);

  const requestRefill = useCallback((pageLimit) => {
    const run = async () => {
      if (!mountedRef.current) return;
      const generation = refillGenerationRef.current;
      setRefilling(true);
      try {
        let offset = knownPendingCountRef.current;
        let hasMorePages = true;
        let addedCards = false;
        while (!addedCards && hasMorePages) {
          const params = { limit: pageLimit, offset };
          if (responsibleFilterId != null) {
            params.responsible_id = responsibleFilterId;
          }
          const response = await mailsService.fetchMailsToSend(params);
          if (
            !mountedRef.current ||
            generation !== refillGenerationRef.current
          ) {
            return;
          }
          const pageResults = Array.isArray(response?.results)
            ? response.results
            : Array.isArray(response)
              ? response
              : [];
          const pageErrors = Array.isArray(response?.errors)
            ? response.errors
            : [];

          const freshCards = pageResults.filter(
            (item) => !knownCardIdsRef.current.has(item.id),
          );
          freshCards.forEach((item) => knownCardIdsRef.current.add(item.id));

          const freshErrors = pageErrors.filter(
            (errorItem) => !knownErrorIdsRef.current.has(errorItem.id),
          );
          freshErrors.forEach((errorItem) =>
            knownErrorIdsRef.current.add(errorItem.id),
          );

          if (!mountedRef.current) return;

          knownPendingCountRef.current += freshCards.length + freshErrors.length;

          if (freshCards.length > 0) {
            setCards((previousCards) => [
              ...previousCards,
              ...freshCards.map(mapCardItem),
            ]);
            addedCards = true;
          }
          if (freshErrors.length > 0) {
            setErrorMails((previousErrors) => [
              ...previousErrors,
              ...freshErrors,
            ]);
          }

          offset += pageLimit;
          hasMorePages =
            typeof response?.count === "number" && offset < response.count;
        }
      } catch (requestError) {
        console.error("Error al cargar mails a revisar:", requestError);
      } finally {
        if (mountedRef.current) setRefilling(false);
      }
    };

    const chained = (refillPromiseRef.current ?? Promise.resolve()).then(
      run,
      run,
    );
    refillPromiseRef.current = chained;
    return chained;
  }, [responsibleFilterId]);

  useEffect(() => {
    mountedRef.current = true;
    refillGenerationRef.current += 1;
    knownCardIdsRef.current = new Set();
    knownErrorIdsRef.current = new Set();
    knownPendingCountRef.current = 0;
    setCards([]);
    setErrorMails([]);
    setApprovalMessages([]);
    setLoading(true);

    requestRefill(BUFFER_SIZE).finally(() => {
      if (mountedRef.current) setLoading(false);
    });

    return () => {
      mountedRef.current = false;
    };
  }, [requestRefill]);

  const resetCard = useCallback(() => {
    setTx(0);
    setRot(0);
    setFly(null);
    setDragging(false);
  }, []);

  const removeTopCard = useCallback(() => {
    setCards((prev) => prev.slice(1));
  }, []);

  const commitSwipe = useCallback(
    (dir) => {
      const topCard = cards[0];
      if (!topCard) {
        resetCard();
        return;
      }

      setFly(dir);
      setDragging(false);

      const markCardNoLongerPending = () => {
        knownPendingCountRef.current -= 1;
      };
      const refillNextCard = () => {
        requestRefill(1);
      };
      const leadName =
        topCard.raw.lead?.lead_name || topCard.raw.lead?.name || "(Sin lead)";
      const addApprovalMessage = (kind, messageText) => {
        if (!mountedRef.current || !messageText) return;
        setApprovalMessages((previousMessages) =>
          [
            ...previousMessages,
            { id: `${topCard.id}-${kind}`, kind, leadName, text: messageText },
          ].slice(-MAX_APPROVAL_MESSAGES),
        );
      };

      if (dir === "right") {
        mailsService
          .approveMailToSend(topCard.id)
          .then((approvalResponse) => {
            markCardNoLongerPending();
            addApprovalMessage("notice", approvalResponse?.detail);
          })
          .catch((err) => {
            console.error("Error aprobando mail_to_send:", err);
            if (serverAnsweredWithClientError(err)) markCardNoLongerPending();
            addApprovalMessage(
              "error",
              extractApiErrorMessage(err) || "No se pudo aprobar el mail.",
            );
          })
          .finally(refillNextCard);
      } else if (dir === "left") {
        mailsService
          .rejectMailToSend(topCard.id)
          .then(markCardNoLongerPending)
          .catch((err) => {
            console.error("Error rechazando mail_to_send:", err);
          })
          .finally(refillNextCard);
      }

      const timer = setTimeout(() => {
        removeTopCard();
        resetCard();
        clearTimeout(timer);
      }, 450);
    },
    [cards, removeTopCard, resetCard, requestRefill],
  );

  const handlePointerMove = useCallback(
    (e) => {
      if (!dragging) return;
      e.preventDefault();
      const clientX = e.clientX ?? startRef.current.x;
      const dx = clientX - startRef.current.x;
      setTx(dx);
      setRot(dx / 12);
    },
    [dragging],
  );

  const handlePointerUp = useCallback(() => {
    if (!dragging) return;
    if (Math.abs(tx) >= THRESHOLD) {
      commitSwipe(tx < 0 ? "left" : "right");
    } else {
      resetCard();
    }
  }, [dragging, tx, commitSwipe, resetCard]);

  const swipeLeft = useCallback(() => {
    if (fly || cards.length === 0) return;
    setTx(-THRESHOLD);
    setRot(-10);
    commitSwipe("left");
  }, [fly, cards.length, commitSwipe]);

  const swipeRight = useCallback(() => {
    if (fly || cards.length === 0) return;
    setTx(THRESHOLD);
    setRot(10);
    commitSwipe("right");
  }, [fly, cards.length, commitSwipe]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (dragging || fly || cards.length === 0) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        swipeLeft();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        swipeRight();
      }
    };
    window.addEventListener("keydown", onKeyDown, { passive: false });
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dragging, fly, cards.length, swipeLeft, swipeRight]);

  if (loading) {
    return (
      <div className={styles.zonaZeroParent}>
        <div className={styles.modalBg} />
        <LoadingScreen
          embedded
          mainText="Cargando mails para revisar..."
          subText=""
        />
      </div>
    );
  }

  return (
    <div
      className={styles.zonaZeroParent}
      onClick={() => {
        setErrorMails([]);
        setApprovalMessages([]);
      }}
    >
      <div className={styles.modalBg} />
      <div
        className={styles.zonaZero}
        onClick={(event) => event.stopPropagation()}
      >
        {cards.length === 0 ? (
          refilling ? (
            <LoadingScreen
              embedded
              mainText="Cargando más mails..."
              subText=""
            />
          ) : (
            <div className={styles.emptyCenter}>
              🎉 No hay más mails para revisar
            </div>
          )
        ) : (
          visible.map((card, i) => {
            const isTop = i === 0;
            const depth = i;
            const translateY = depth * 24;
            const scale = 1 - depth * 0.04;
            const opacity = 1 - depth * 0.18;

            const style = isTop
              ? {
                  transform: `translateX(${tx}px) rotate(${rot}deg)`,
                  zIndex: 30 - depth,
                  WebkitUserSelect: "none",
                }
              : {
                  transform: `translateY(${translateY}px) scale(${scale})`,
                  opacity,
                  zIndex: 30 - depth,
                };

            const safeHtml = sanitizeHtml(card.body || "");
            const responsible = card.raw.responsible;
            const accountName = card.raw.mail_account?.name || responsible?.account?.name;
            const lead = card.raw.lead;
            const leadName = lead?.lead_name || lead?.name;
            const leadEmail = lead?.email;
            const leadCompany = lead?.account?.name;
            const recipients = Array.isArray(card.raw.recipients)
              ? card.raw.recipients
              : [];
            const dateLabel = formatDate(card.raw.created_at || card.raw.date);

            return (
              <div
                key={card.id}
                className={[
                  styles.pillParent,
                  isTop ? "" : styles.underCard,
                  fly === "left" && isTop ? styles.flyLeft : "",
                  fly === "right" && isTop ? styles.flyRight : "",
                ].join(" ")}
                style={style}
                onSelectStart={(e) => e.preventDefault()}
                onMouseDownCapture={(e) => {
                  if (!e.target.closest("button")) e.preventDefault();
                }}
                onDragStart={(e) => e.preventDefault()}
                onPointerDown={(e) => {
                  if (!isTop || fly) return;
                  if (e.target.closest("button")) return;
                  e.preventDefault();
                  startRef.current.x = e.clientX ?? 0;
                  setDragging(true);
                  try {
                    e.currentTarget.setPointerCapture(e.pointerId);
                  } catch {
                    // ignore
                  }
                }}
                onPointerMove={(e) => {
                  if (!isTop) return;
                  handlePointerMove(e);
                }}
                onPointerUp={(e) => {
                  if (!isTop) return;
                  try {
                    e.currentTarget.releasePointerCapture(e.pointerId);
                  } catch {
                    // ignore
                  }
                  handlePointerUp();
                }}
                onPointerCancel={(e) => {
                  if (!isTop) return;
                  try {
                    e.currentTarget.releasePointerCapture(e.pointerId);
                  } catch {
                    // ignore
                  }
                  handlePointerUp();
                }}
              >
                {isTop && (
                  <>
                    <button
                      type="button"
                      className={styles.pill}
                      onClick={(e) => {
                        e.stopPropagation();
                        swipeLeft();
                      }}
                      aria-label="Rechazar"
                    >
                      <div className={styles.iconscross}>
                        <img
                          src={cross}
                          alt="Cancelar"
                          className={styles.iconscrossChild}
                          draggable={false}
                        />
                      </div>
                    </button>

                    <button
                      type="button"
                      className={styles.zonaZeroParentPill}
                      onClick={(e) => {
                        e.stopPropagation();
                        swipeRight();
                      }}
                      aria-label="Aprobar"
                    >
                      <div className={styles.iconstick}>
                        <img
                          src={tick}
                          alt="Aprobar"
                          className={styles.iconstickChild}
                          draggable={false}
                        />
                      </div>
                    </button>
                  </>
                )}

                <div className={styles.cardHeader}>
                  <div className={styles.senderLeft}>
                    <div className={styles.senderRow}>
                      <Avatar
                        src={responsible?.account?.photo ?? responsible?.avatar_url}
                        name={responsible?.name}
                        size={24}
                      />
                      {responsible?.name && (
                        <span className={styles.senderName}>{responsible.name}</span>
                      )}
                      {accountName && (
                        <span className={styles.accountTag}>{accountName}</span>
                      )}
                    </div>
                    <div className={styles.receptorLine}>
                      <span className={styles.dimText}>to </span>
                      {leadName && <span>{leadName}</span>}
                      {leadEmail && (
                        <span className={styles.dimText}> &lt;{leadEmail}&gt;</span>
                      )}
                      {leadCompany && (
                        <span className={styles.dimText}> from {leadCompany}</span>
                      )}
                      <span className={styles.dimText}> ▾</span>
                    </div>
                    {recipients.length > 0 && (
                      <ul
                        className={styles.recipientList}
                        aria-label="Destinatarios y su validación"
                      >
                        {recipients.map((recipient) => (
                          <li
                            key={`${recipient.role}-${recipient.email}`}
                            className={[
                              styles.recipientChip,
                              styles[`recipientState_${recipient.state}`] || "",
                            ].join(" ")}
                          >
                            {recipient.role === "cc" && (
                              <span className={styles.recipientRole}>cc</span>
                            )}
                            <span>{recipient.email}</span>
                            <span className={styles.recipientStateLabel}>
                              {recipientStateLabel(recipient)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {dateLabel && (
                    <span className={styles.cardDate}>{dateLabel}</span>
                  )}
                </div>

                <div className={styles.cardSubject}>{card.subject}</div>
                <div className={styles.mailStatus}>Mail preparado para envío</div>

                {safeHtml ? (
                  <div
                    className={styles.mailBody}
                    dangerouslySetInnerHTML={{ __html: safeHtml }}
                  />
                ) : (
                  <div className={styles.mailBody}>Mail to review</div>
                )}
              </div>
            );
          })
        )}
      </div>
      {approvalMessages.length > 0 && (
        <div
          className={styles.approvalPanel}
          role="status"
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.errorPanelTitle}>Avisos de la aprobación</div>
          {approvalMessages.map((approvalMessage) => (
            <div
              key={approvalMessage.id}
              className={[
                styles.errorRow,
                approvalMessage.kind === "error" ? styles.approvalError : "",
              ].join(" ")}
            >
              <span className={styles.errorLeadName}>
                {approvalMessage.leadName}
              </span>
              <span
                className={styles.approvalMessageText}
                title={approvalMessage.text}
              >
                {approvalMessage.text}
              </span>
            </div>
          ))}
        </div>
      )}
      {errorMails.length > 0 && (
        <div
          className={styles.errorPanel}
          onClick={(event) => event.stopPropagation()}
        >
          <div className={styles.errorPanelTitle}>
            ⚠️{" "}
            {errorMails.length === 1
              ? "1 mail no se pudo preparar"
              : `${errorMails.length} mails no se pudieron preparar`}
          </div>
          {errorMails.map((errorMail) => (
            <div key={errorMail.id} className={styles.errorRow}>
              <span className={styles.errorLeadName}>
                {errorMail.lead?.lead_name ||
                  errorMail.lead?.name ||
                  "(Sin lead)"}
              </span>
              {errorMail.lead?.email && (
                <span className={styles.errorLeadEmail}>
                  &lt;{errorMail.lead.email}&gt;
                </span>
              )}
              <span className={styles.errorMessage} title={errorMail.error}>
                {errorMail.error}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MailFlyer;
